import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { FileText, Calendar, DollarSign, MessageCircle, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import API_URL from '../../utils/apiUrl';

const OperatorPayslips = () => {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/operator/payslips`, { headers: getAuthHeader() });
        setPayslips(res.data.payslips || []);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetch();
  }, []);

  const totalEarnings = payslips.reduce((s, p) => s + (p.total_earning || 0), 0);

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8" data-testid="operator-payslips">
      <div className="max-w-3xl mx-auto space-y-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/operator/inbox')} data-testid="back-btn">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-slate-900">My Payslips</h1>
            <p className="text-sm text-slate-500">Weekly earnings summary</p>
          </div>
        </div>

        <Card>
          <CardContent className="pt-5">
            <div className="flex items-center gap-6">
              <div>
                <div className="text-sm text-slate-500">Total Earnings</div>
                <div className="text-2xl font-bold text-green-700">${totalEarnings.toFixed(2)}</div>
              </div>
              <div>
                <div className="text-sm text-slate-500">Payslips</div>
                <div className="text-2xl font-bold text-slate-800">{payslips.length}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <p className="text-center py-8 text-slate-400">Loading...</p>
        ) : payslips.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-slate-400">
              <FileText className="h-10 w-10 mx-auto mb-2 opacity-50" />
              <p>No payslips yet</p>
            </CardContent>
          </Card>
        ) : payslips.map(p => (
          <Card key={p.id} data-testid={`payslip-${p.id}`}>
            <CardContent className="pt-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-50">
                    <FileText className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <div className="font-medium text-slate-800">{p.period_label}</div>
                    <div className="flex items-center gap-3 text-sm text-slate-500 mt-0.5">
                      <span className="flex items-center gap-1">
                        <MessageCircle className="h-3 w-3" /> {p.messages_sent || 0} messages
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" /> {new Date(p.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-green-700">${(p.total_earning || 0).toFixed(2)}</div>
                  <Badge className="bg-green-100 text-green-700 text-xs">{p.status}</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default OperatorPayslips;
